import React from 'react';
import Screen01Projects from './Screen01Projects';
import Screen02ProjectDetail from './Screen02ProjectDetail';
import Screen03Upload from './Screen03Upload';
import Screen04Transfers from './Screen04Transfers';
import Screen05Board from './Screen05Board';
import Screen06Auth from './Screen06Auth';
import Screen07Boards from './Screen07Boards';
import Screen08Share from './Screen08Share';
import Screen09Stems from './Screen09Stems';
import Screen10Landing from './Screen10Landing';
import Screen11LightMode from './Screen11LightMode';
import Screen12Billing from './Screen12Billing';
import Screen13States from './Screen13States';
import Screen14SyncStatus from './Screen14SyncStatus';
import Screen15Register from './Screen15Register';
import Screen17DialogsMenus from './Screen17DialogsMenus';

/**
 * Registry of the seventeen Signal System surfaces, in the order they appear in
 * the "StudioVault Screens" design project.
 *
 * Screens are being built one at a time; a screen with no `Component` yet
 * renders a placeholder from the route rather than 404ing, so the index gallery
 * always reflects the full design and shows what is left to do.
 */
export interface SignalScreenDef {
  /** URL segment, e.g. `/signal/projects`. */
  id: string;
  /** Two-digit design index, e.g. "01". */
  index: string;
  title: string;
  /** Mono caption from the design canvas. */
  note?: string;
  /** One-line description for the gallery card. */
  summary: string;
  Component?: React.ComponentType;
}

export const signalScreens: SignalScreenDef[] = [
  {
    id: 'projects',
    index: '01',
    title: 'Projects grid',
    note: 'DESKTOP · DARK',
    summary:
      'App shell — sidebar, vault meter, glass header, filterable grid of project cards.',
    Component: Screen01Projects,
  },
  {
    id: 'project-detail',
    index: '02',
    title: 'Project detail',
    note: 'FILES · VERSIONS · SYNC',
    summary:
      'Waveform player, file tree, version history timeline, sync state and people.',
    Component: Screen02ProjectDetail,
  },
  {
    id: 'upload',
    index: '03',
    title: 'Upload flow',
    note: 'STEP 1 CHOOSER · STEP 2 DETECTED',
    summary:
      'Project-vs-stems chooser, then the detected-sessions review step.',
    Component: Screen03Upload,
  },
  {
    id: 'transfers',
    index: '04',
    title: 'Transfer sidebar',
    note: 'DARK · LIGHT',
    summary:
      'Live transfer queue with uploads, downloads, queue and session history.',
    Component: Screen04Transfers,
  },
  {
    id: 'board',
    index: '05',
    title: 'Collaboration board',
    note: 'KANBAN · DRAG STATE SHOWN',
    summary: 'Four-column kanban over projects, with a card mid-drag.',
    Component: Screen05Board,
  },
  {
    id: 'auth',
    index: '06',
    title: 'Auth & first run',
    note: 'SIGN IN · DESKTOP HANDOFF · PICK FOLDER',
    summary: 'Sign-in, desktop auth handoff, and the folder-roots picker.',
    Component: Screen06Auth,
  },
  {
    id: 'boards',
    index: '07',
    title: 'Boards list',
    summary: 'Gallery of boards with accent rails and collaborator stacks.',
    Component: Screen07Boards,
  },
  {
    id: 'share',
    index: '08',
    title: 'Share & invite',
    note: 'DIALOG · ROLE PICKER OPEN',
    summary: 'Share dialog with the role menu open, plus the invites inbox.',
    Component: Screen08Share,
  },
  {
    id: 'stems',
    index: '09',
    title: 'Stems pack detail',
    note: 'NO DAW · PER-STEM SOLO / MUTE',
    summary: 'Stem pack transport with per-stem waveforms, solo and mute.',
    Component: Screen09Stems,
  },
  {
    id: 'landing',
    index: '10',
    title: 'Landing page',
    note: 'PUBLIC · ABOVE THE FOLD + PROOF',
    summary:
      'Public marketing surface — hero, proof card, features, how it works, collaboration, pricing, FAQ.',
    Component: Screen10Landing,
  },
  {
    id: 'light',
    index: '11',
    title: 'Light mode — grid & detail',
    note: 'SAME TOKENS, LIGHT SET',
    summary: 'Projects grid and project detail rebuilt on the light token set.',
    Component: Screen11LightMode,
  },
  {
    id: 'billing',
    index: '12',
    title: 'Account & billing',
    note: 'SETTINGS · UPGRADE DIALOG',
    summary: 'Settings panel with plan, storage breakdown and invoices.',
    Component: Screen12Billing,
  },
  {
    id: 'states',
    index: '13',
    title: 'Empty & error states',
    note: 'THE UNHAPPY PATHS',
    summary:
      'Empty vault, version conflict, agent offline and a failed transfer.',
    Component: Screen13States,
  },
  {
    id: 'sync',
    index: '14',
    title: 'Sync status set',
    note: 'CLASSIFICATION · COMMIT · RECOVERY',
    summary:
      'The six file sync states with their rules, plus commit, recovery and toasts.',
    Component: Screen14SyncStatus,
  },
  {
    id: 'register',
    index: '15',
    title: 'Download and register',
    note: 'DESKTOP AGENT · SIGN UP',
    summary: 'Desktop agent download page beside the create-account form.',
    Component: Screen15Register,
  },
  {
    id: 'dialogs',
    index: '17',
    title: 'Dialogs and menus',
    note: 'COMMIT · PROMPTS · MENUS · MOBILE',
    summary:
      'Commit dialog, folder prompts, the file actions menu, payment methods and mobile.',
    Component: Screen17DialogsMenus,
  },
];

export const findSignalScreen = (id?: string): SignalScreenDef | undefined =>
  signalScreens.find((screen) => screen.id === id);
